<script>
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';

  import ClauseRow from './clause-row.svelte';
  import { createEmptyClause, JOINS } from './liquid-builder';

  /**
   * Builder orchestrator: renders N clause rows + N-1 join pickers and the
   * "Add condition" button. Strictly prop-down / event-up — every state
   * mutation is shipped out via `onChange(newState)` with a freshly cloned
   * `clauses` / `joins` array.
   *
   * Invariant: state.joins.length === state.clauses.length - 1.
   */
  export let state = { clauses: [createEmptyClause()], joins: [] };
  export let availableFields = [];
  export let onChange = () => {};

  $: clauses = state?.clauses ?? [];
  $: joins = state?.joins ?? [];

  $: joinOptions = JOINS.map(({ id, labelKey }) => ({ label: $format(labelKey), value: id }));

  const joinOptionFor = (joinId) => joinOptions.find((opt) => opt.value === joinId) || joinOptions[0];

  const emit = (nextClauses, nextJoins) => {
    onChange({ ...state, clauses: nextClauses, joins: nextJoins });
  };

  const updateClause = (index, updatedClause) => {
    const next = clauses.slice();
    next[index] = updatedClause;
    emit(next, joins.slice());
  };

  const removeClause = (index) => {
    if (clauses.length <= 1) return;

    const nextClauses = clauses.slice();
    nextClauses.splice(index, 1);

    const nextJoins = joins.slice();
    // joins[i] sits between clauses[i] and clauses[i+1]. Removing clauses[i]
    // drops one join: the one to the LEFT (i-1) when i > 0, otherwise the one
    // to the RIGHT (i=0). This keeps joins.length === clauses.length - 1.
    const joinSlotToDrop = index === 0 ? 0 : index - 1;
    nextJoins.splice(joinSlotToDrop, 1);

    emit(nextClauses, nextJoins);
  };

  const updateJoin = (index, joinValue) => {
    const nextJoins = joins.slice();
    nextJoins[index] = joinValue;
    emit(clauses.slice(), nextJoins);
  };

  const addClause = () => {
    const nextClauses = clauses.concat(createEmptyClause());
    const nextJoins = clauses.length === 0 ? [] : joins.concat('and');
    emit(nextClauses, nextJoins);
  };
</script>

<div class="builder">
  {#each clauses as clause, index (clause._id)}
    {#if index > 0}
      <div class="join-row">
        <span class="join-picker">
          <Select
            items={joinOptions}
            value={joinOptionFor(joins[index - 1])}
            on:change={(event) => updateJoin(index - 1, event?.detail?.value ?? 'and')}
            clearable={false}
          />
        </span>
      </div>
    {/if}

    <ClauseRow
      {clause}
      {availableFields}
      canRemove={clauses.length > 1}
      onChange={(updated) => updateClause(index, updated)}
      onRemove={() => removeClause(index)}
    />
  {/each}

  <div class="builder-add-row">
    <Button color="link" class="add-condition" on:click={addClause}>
      <Icon name="plus" />
      <span>{$format('label.ADD_CONDITION')}</span>
    </Button>
  </div>
</div>

<style lang="scss" src="./builder.scss"></style>
